import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Flex } from '@decisiv/ui-components';
import SelectMenu   from '@decisiv/ui-components/lib/components/SelectMenu';
import SearchInput  from '@decisiv/ui-components/lib/components/SearchInput';
import Tag          from '@decisiv/ui-components/lib/components/Tag';
import iconix       from '@decisiv/iconix';

/**
 * SelectMenu renders its `label` prop as a plain <span> beside the trigger
 * button, making the label text a non-interactive dead zone. To give each
 * filter a single fully-clickable control we:
 *
 *   1. Pass label=" " so the built-in span is empty.
 *   2. Hide that span via CSS (Wrapper targets the first-child span of each
 *      SelectMenu's inner Flex div).
 *   3. Prepend an "All Xs" sentinel option (id='') — the button always shows
 *      either "All Subscription Types" or the selected value, with the full
 *      button area being clickable.
 */
const SearchRow = styled(Flex)`
  margin-bottom: 8px;
`;

const FilterRow = styled(Flex)`
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;

  /* SelectMenu renders: FilterRow > div(Flex) > span(label) + button + DropdownList */
  & > div > span:first-child {
    display: none;
  }
`;

const TagRow = styled(Flex)`
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

// Tag's className prop lands on its Container element, so these selectors are
// scoped to this component only. Two internal constraints need neutralising:
// 1. Content (first child div) has `max-width: calc(100% - 22px)` which
//    collapses to ~0 during flex intrinsic-size computation.
// 2. The text span has `flex-shrink: 1` and a hardcoded `max-width: ~267px`
//    that squeeze it regardless of available space.
const FilterTag = styled(Tag)`
  & > div:first-child { max-width: none !important; }
  & > div:first-child > span { flex-shrink: 0 !important; max-width: none !important; }
`;

const ClearLink = styled.a`
  font-size: 13px;
  color: #1a73c8;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  &:hover { text-decoration: underline; }
`;

/**
 * Row of filter controls — a SearchInput (with field selector) and SelectMenus.
 *
 * - Filters without `options` appear in the SearchInput field dropdown; the
 *   user picks which field to search, types a value, and presses Enter (or
 *   the search button) to apply. Clearing the input immediately removes that
 *   filter.
 * - Filters with `options` render as SelectMenu (fires immediately on selection).
 * - A "Clear filters" link appears when any filter is active.
 *
 * filterConfig item shape:
 *   { name, label, options?: Array<{label, value}> | (filterProps) => Array }
 */
const resolveOptions = ({ label, options, allLabel }, filterProps) => {
  const raw = typeof options === 'function' ? options(filterProps) : (options ?? []);
  const normalised = raw.map((opt) => ({
    id:    String(opt.value ?? opt.id ?? ''),
    label: opt.label,
  }));
  return [{ id: '', label: allLabel ?? `All ${label}` }, ...normalised];
};

const AdminFilters = ({ filterConfig, onFilter, filterProps = {} }) => {
  const textFilters   = filterConfig.filter((f) => !f.options);
  const selectFilters = filterConfig.filter((f) => f.options);

  const [values,      setValues]      = useState({});
  const [searchField, setSearchField] = useState(textFilters[0]?.name ?? '');
  const [searchText,  setSearchText]  = useState('');

  // ── SelectMenu helpers ───────────────────────────────────────────────────

  const resolvedOptionsMap = useMemo(
    () => new Map(selectFilters.map((f) => [f.name, resolveOptions(f, filterProps)])),
    [selectFilters, filterProps],
  );

  const handleSelectChange = (name, id) => {
    const next = { ...values };
    if (id) next[name] = id;
    else    delete next[name];
    setValues(next);
    onFilter(next);
  };

  // ── SearchInput helpers ──────────────────────────────────────────────────

  // Called when the user switches the field dropdown inside SearchInput.
  // The input shows whatever active value already exists for the new field.
  const handleSearchByChange = (fieldId) => {
    setSearchField(fieldId);
    setSearchText(values[fieldId] ?? '');
  };

  // Called on every keystroke. When text is cleared (X button or backspace-
  // to-empty) the filter for the current field is removed immediately.
  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    if (!text) {
      const next = { ...values };
      delete next[searchField];
      setValues(next);
      onFilter(next);
    }
  };

  // Called when the user submits (Enter or search button).
  const handleSearch = (text) => {
    const next = { ...values };
    if (text) {
      next[searchField] = text;
    } else {
      delete next[searchField];
    }
    setValues(next);
    onFilter(next);
  };

  // ── Tag removal ──────────────────────────────────────────────────────────

  const handleRemoveTag = (name) => {
    const next = { ...values };
    delete next[name];
    setValues(next);
    if (name === searchField) setSearchText('');
    onFilter(next);
  };

  // ── Shared clear ─────────────────────────────────────────────────────────

  const handleClear = (e) => {
    e.preventDefault();
    setValues({});
    setSearchText('');
    onFilter({});
  };

  const hasActiveFilter = Object.values(values).some(Boolean);

  const activeTags = [
    ...textFilters
      .filter((f) => values[f.name])
      .map((f) => ({ name: f.name, label: f.label, displayValue: values[f.name] })),
    ...selectFilters
      .filter((f) => values[f.name])
      .map((f) => {
        const option = resolvedOptionsMap.get(f.name)?.find((o) => o.id === values[f.name]);
        return { name: f.name, label: f.label, displayValue: option?.label ?? values[f.name] };
      }),
  ];

  if (textFilters.length === 0 && selectFilters.length === 0) return null;

  // SearchInput.searchByOptions uses Select internally; Select matches options
  // by `id` and calls onChange with the selected option's `id`.
  const searchByOptions = textFilters.map((f) => ({ id: f.name, label: f.label }));

  return (
    <>
      {textFilters.length > 0 && (
        <SearchRow>
          <SearchInput
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            searchOn="submit"
            searchByOptions={searchByOptions}
            searchByValue={searchField}
            onSearchByChange={handleSearchByChange}
          />
        </SearchRow>
      )}

      {selectFilters.length > 0 && (
        <FilterRow>
          {selectFilters.map((filter) => (
            <SelectMenu
              key={filter.name}
              label=" "
              options={resolvedOptionsMap.get(filter.name)}
              selected={values[filter.name] ?? ''}
              onChange={(id) => handleSelectChange(filter.name, id)}
              size="small"
            />
          ))}
        </FilterRow>
      )}

      {hasActiveFilter && (
        <TagRow>
          {activeTags.map((tag) => (
            <FilterTag
              key={tag.name}
              text={`${tag.label}: ${tag.displayValue}`}
              size="small"
              action={() => handleRemoveTag(tag.name)}
              actionIcon={iconix.Times}
              actionIntent="normal"
              actionAttributes={{ 'aria-label': `Remove ${tag.label} filter` }}
            />
          ))}

          <ClearLink href="#" onClick={handleClear}>
            Clear filters
          </ClearLink>
        </TagRow>
      )}
    </>
  );
};

export default AdminFilters;
